import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import {
  useGetSheqExpiringQuery,
  useGetSheqIncidentSummaryQuery,
  useGetSheqPpeRecordsQuery,
  SheqPpeRecord,
} from "../../../redux/sheqApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";

const NAVY = "#123456";

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Expiring report ─────────────────────────────────────────────────────────
const ExpiringReport: React.FC = () => {
  const [days, setDays] = useState(30);
  const [blockId, setBlockId] = useState("");
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const { data: expiring = [], isLoading } = useGetSheqExpiringQuery({
    days,
    blockId: blockId || undefined,
  });

  const handleDownload = () => {
    const header = [
      "Worker",
      "Employee No",
      "Block",
      "PPE Item",
      "Code",
      "Size",
      "Issued Date",
      "Due Date",
      "Days Remaining",
    ];
    const rows = expiring.map((r: SheqPpeRecord) => {
      const daysLeft = Math.round(
        (new Date(`${r.replacementDueDate}T00:00:00`).getTime() -
          new Date(today() + "T00:00:00").getTime()) /
          86400000,
      );
      return [
        r.workerName,
        r.workerEmployeeNo ?? "",
        r.blockName ?? "",
        r.ppeName,
        r.ppeCode ?? "",
        r.size ?? "",
        r.issuedDate,
        r.replacementDueDate,
        String(daysLeft),
      ];
    });
    downloadCsv(`PPE_Expiring_${today()}.csv`, [header, ...rows]);
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="flex-end">
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Expiring within</InputLabel>
          <Select
            label="Expiring within"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[7, 14, 30, 60, 90].map((d) => (
              <MenuItem key={d} value={d}>
                {d} days
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 180 }}>
          <InputLabel>Site / Block</InputLabel>
          <Select
            label="Site / Block"
            value={blockId}
            onChange={(e) => setBlockId(e.target.value)}
          >
            <MenuItem value="">All sites</MenuItem>
            {blocks.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownload}
          disabled={expiring.length === 0}
          size="small"
        >
          CSV
        </Button>
      </Box>

      {isLoading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : expiring.length === 0 ? (
        <Typography color="text.secondary">
          No items expiring within {days} days.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                {[
                  "Worker",
                  "Block",
                  "PPE Item",
                  "Size",
                  "Issued",
                  "Due Date",
                  "Days Left",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#555",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expiring.map((r: SheqPpeRecord) => {
                const daysLeft = Math.round(
                  (new Date(`${r.replacementDueDate}T00:00:00`).getTime() -
                    new Date(today() + "T00:00:00").getTime()) /
                    86400000,
                );
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 12px", fontSize: 13 }}>
                      {r.workerName}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 13 }}>
                      {r.blockName ?? "—"}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 13 }}>
                      {r.ppeName}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 13 }}>
                      {r.size ?? "—"}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 13 }}>
                      {r.issuedDate}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 13 }}>
                      {r.replacementDueDate}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        fontSize: 13,
                        color:
                          daysLeft <= 7
                            ? "#c62828"
                            : daysLeft <= 14
                            ? "#f57c00"
                            : "#2e7d32",
                        fontWeight: 600,
                      }}
                    >
                      {daysLeft <= 0 ? "Overdue" : `${daysLeft}d`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

// ─── Incident summary ─────────────────────────────────────────────────────────
const IncidentSummaryReport: React.FC = () => {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [blockId, setBlockId] = useState("");
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const { data: summary, isLoading } = useGetSheqIncidentSummaryQuery({
    from,
    to,
    blockId: blockId || undefined,
  });

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="flex-end">
        <FormControl size="small" sx={{ width: 180 }}>
          <InputLabel>Site / Block</InputLabel>
          <Select
            label="Site / Block"
            value={blockId}
            onChange={(e) => setBlockId(e.target.value)}
          >
            <MenuItem value="">All sites</MenuItem>
            {blocks.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box component="label" sx={{ fontSize: 12, color: "#555" }}>
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ marginLeft: 6, fontSize: 13 }}
          />
        </Box>
        <Box component="label" sx={{ fontSize: 12, color: "#555" }}>
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={{ marginLeft: 6, fontSize: 13 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : !summary ? null : (
        <Box display="flex" gap={3} flexWrap="wrap">
          <Card sx={{ flex: "1 1 200px" }}>
            <CardContent>
              <Typography variant="h3" fontWeight={700} color={NAVY}>
                {summary.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total incidents
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                Days lost: <b>{summary.totalDaysLost}</b>
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: "1 1 200px" }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                By Type
              </Typography>
              {Object.entries(summary.byType).map(([k, v]) => (
                <Box key={k} display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {k.replace(/_/g, " ")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {v}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card sx={{ flex: "1 1 180px" }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                By Severity
              </Typography>
              {Object.entries(summary.bySeverity).map(([k, v]) => (
                <Box key={k} display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {k}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {v}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card sx={{ flex: "1 1 180px" }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                By Status
              </Typography>
              {Object.entries(summary.byStatus).map(([k, v]) => (
                <Box key={k} display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {k.replace(/_/g, " ")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {v}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

// ─── Full PPE register export ─────────────────────────────────────────────────
const RegisterExport: React.FC = () => {
  const [blockId, setBlockId] = useState("");
  const [status, setStatus] = useState("");
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const { data: records = [], isLoading } = useGetSheqPpeRecordsQuery({
    blockId: blockId || undefined,
    status: status || undefined,
  });

  const handleDownload = () => {
    const header = [
      "Worker",
      "Employee No",
      "Department",
      "Block",
      "PPE Item",
      "Code",
      "Size",
      "Qty",
      "Batch/Serial",
      "Issued Date",
      "Due Date",
      "Status",
      "Condition",
      "Issued By",
      "Notes",
    ];
    const rows = records.map((r: SheqPpeRecord) => [
      r.workerName,
      r.workerEmployeeNo ?? "",
      r.department ?? "",
      r.blockName ?? "",
      r.ppeName,
      r.ppeCode ?? "",
      r.size ?? "",
      String(r.quantity),
      r.batchOrSerialNumber ?? "",
      r.issuedDate,
      r.replacementDueDate,
      r.status,
      r.condition,
      r.issuedByName ?? "",
      r.notes ?? "",
    ]);
    downloadCsv(`PPE_Register_${today()}.csv`, [header, ...rows]);
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="flex-end">
        <FormControl size="small" sx={{ width: 180 }}>
          <InputLabel>Site / Block</InputLabel>
          <Select
            label="Site / Block"
            value={blockId}
            onChange={(e) => setBlockId(e.target.value)}
          >
            <MenuItem value="">All sites</MenuItem>
            {blocks.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">All statuses</MenuItem>
            {["ACTIVE", "RETURNED", "EXPIRED", "DAMAGED", "LOST"].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownload}
          disabled={isLoading || records.length === 0}
          size="small"
        >
          Download CSV ({records.length} rows)
        </Button>
      </Box>
      {isLoading && <Typography color="text.secondary">Loading…</Typography>}
    </Box>
  );
};

// ─── Main reports tab ─────────────────────────────────────────────────────────
const REPORT_TABS = [
  { label: "Expiring PPE" },
  { label: "Incident Summary" },
  { label: "Register Export" },
];

export const SheqReports: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={3}>
        Reports
      </Typography>

      <Box display="flex" gap={1} mb={4} borderBottom="1px solid #e0e0e0">
        {REPORT_TABS.map((t, i) => (
          <Box
            key={i}
            onClick={() => setTab(i)}
            sx={{
              px: 2,
              py: 1.2,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: tab === i ? 700 : 400,
              color: tab === i ? NAVY : "text.secondary",
              borderBottom:
                tab === i ? `2px solid ${NAVY}` : "2px solid transparent",
              mb: "-1px",
            }}
          >
            {t.label}
          </Box>
        ))}
      </Box>

      {tab === 0 && <ExpiringReport />}
      {tab === 1 && <IncidentSummaryReport />}
      {tab === 2 && <RegisterExport />}
    </Box>
  );
};
